import { SelectOption } from '../../model/selectOption'

export type TreeSelectOption = {
  value: string
  name: string
  iconUrl?: string
  tooltip?: string
  parentValue: string | undefined
  children: TreeSelectOption[]
}

export const buildTree = (
  options: SelectOption[],
  parentMap: Record<string, string>
): TreeSelectOption[] => {
  const nodeMap = new Map<string, TreeSelectOption>()
  for (const opt of options) {
    nodeMap.set(opt.value, {
      value: opt.value,
      name: opt.name,
      iconUrl: opt.iconUrl,
      tooltip: opt.tooltip,
      parentValue: parentMap[opt.value],
      children: [],
    })
  }

  const roots: TreeSelectOption[] = []
  for (const node of nodeMap.values()) {
    const parent = node.parentValue ? nodeMap.get(node.parentValue) : undefined
    if (parent) {
      parent.children.push(node)
    } else {
      roots.push(node)
    }
  }

  return roots
}

export const countTreeNodes = (roots: TreeSelectOption[]): number => {
  let count = 0
  const countNode = (node: TreeSelectOption) => {
    count++
    node.children.forEach(countNode)
  }
  roots.forEach(countNode)
  return count
}

const collectAncestors = (
  value: string,
  parentMap: Record<string, string>
): string[] => {
  const parent = parentMap[value]
  return parent ? [parent, ...collectAncestors(parent, parentMap)] : []
}

// 開いた直後は常にルート階層（ワークグループ）のみ展開し、初期描画コストを抑える。
// それより下の階層はユーザーがクリックして自分で開く。
// 「ルート」の判定はbuildTreeの結果をそのまま使う（親のuuidを持っていても、
// その親がoptionsに実在しない場合はbuildTree側もルート扱いにしているため、
// ここで独自に判定基準を持つとbuildTreeの描画結果とズレる）。
// 既に選択済みの項目がある場合はその祖先も合わせて展開し、開き直したときに
// 選択済みの項目が折りたたまれて見えなくならないようにする（展開ノードは
// 選択件数分しか増えないため、件数が多いモードでも重くならない）
export const computeInitialExpandedNodes = (
  options: SelectOption[],
  parentMap: Record<string, string>,
  selectedValues: Set<string> = new Set()
): Set<string> => {
  const rootUuids = buildTree(options, parentMap).map(root => root.value)
  const ancestorUuids = [...selectedValues].flatMap(value =>
    collectAncestors(value, parentMap)
  )
  return new Set([...rootUuids, ...ancestorUuids])
}

export const isDescendantOf = (
  nodeValue: string,
  ancestorValue: string,
  parentMap: Record<string, string>
): boolean => {
  let current = parentMap[nodeValue]
  while (current) {
    if (current === ancestorValue) return true
    current = parentMap[current]
  }
  return false
}

export const filterTree = (
  roots: TreeSelectOption[],
  filterText: string
): TreeSelectOption[] => {
  if (!filterText) return roots

  const lower = filterText.toLowerCase()

  const filterNode = (node: TreeSelectOption): TreeSelectOption | undefined => {
    const filteredChildren = node.children
      .map(filterNode)
      .filter((c): c is TreeSelectOption => c !== undefined)

    const selfMatches = node.name.toLowerCase().includes(lower)
    if (selfMatches || filteredChildren.length > 0) {
      return { ...node, children: filteredChildren }
    }
    return undefined
  }

  return roots
    .map(filterNode)
    .filter((n): n is TreeSelectOption => n !== undefined)
}
